import { AxiosError, AxiosResponse } from 'axios';

export enum TaskStatusType {
  Todo = 0,
  OnHold = 1,
  InProgress = 2,
  Resolved = 3,
  Invalid = 4,
}

export enum TaskPriorityType {
  Urgent = 0,
  Important = 1,
  Medium = 2,
  Low = 3,
}

export type TApiTaskEntity = {
  id: number;
  title: string;
  description: string;
  dueDate: string | null;
  statusLabel: string;
  statusType: TaskStatusType | null;
  statusColor: string;
  priorityLabel: string;
  priorityType: TaskPriorityType | null;
  priorityColor: string;
  isRecurring: boolean;
  serviceTaskType?: number | null;
  serviceTaskTypeName?: string | null;
  parentEquipmentId?: number | null;
  parentEquipmentName?: string | null;
};

export type TApiTaskDetailEntity = TApiTaskEntity & {
  startDate: string | null;
  budget: string;
  scheduleDescription: string;
  statusId: number | null;
  priorityId: number | null;
  parentTitle: string;
  assigneeName: string;
  assigneeEmail: string | null;
  parentEquipmentImage?: string | null;
  parentEquipmentModel?: string | null;
  parentEquipmentSerialVin?: string | null;
  conclusion?: boolean;
  conclusionStatement?: string | null;
  conclusionStatus?: number | null;
  conclusionStatusValue?: number | null;
  conclusionStatusLabel?: string | null;
  conclusionDateFormatted?: string | null;
  conclusionUser?: string | null;
};

export type TTaskComment = {
  id: number;
  authorName: string;
  comment: string;
  createdAt: string;
  isOwnComment: boolean;
};

export type TTaskFile = {
  id: number;
  filename: string;
  mimeType: string;
  url: string;
  isOwnFile: boolean;
};

export type TTaskStatusOption = {
  id: number;
  label: string;
  type: TaskStatusType | null;
  color: string;
};

export type TTaskPriorityOption = {
  id: number;
  label: string;
  type: TaskPriorityType | null;
  color: string;
};

export type TTaskAssigneeOption = {
  email: string;
  name: string;
};

export type TTaskDetailResponse = {
  task: TApiTaskDetailEntity;
  comments: TTaskComment[];
  files: TTaskFile[];
  statuses: TTaskStatusOption[];
  priorities: TTaskPriorityOption[];
  assignees: TTaskAssigneeOption[];
  hasUnreadComments: boolean;
  unreadCommentCount: number;
  assigneeChanged?: boolean;
};

export type TTaskListOption = {
  label: string;
  type: number | null;
};

export interface TTasksState {
  readonly loading?: boolean;
  readonly data?: Array<TApiTaskEntity>;
  readonly filteredList?: Array<TApiTaskEntity>;
  readonly statusOptions?: Array<TTaskListOption>;
  readonly priorityOptions?: Array<TTaskListOption>;
  readonly searchPhrase?: string;
  readonly selectedStatuses?: Array<TaskStatusType>;
  readonly selectedPriorities?: Array<TaskPriorityType>;
  readonly detailLoading?: boolean;
  readonly detail?: TTaskDetailResponse;
}

export const DEFAULT_STATUS_FILTER: TaskStatusType[] = [
  TaskStatusType.Todo,
  TaskStatusType.OnHold,
  TaskStatusType.InProgress,
];

export const DEFAULT_PRIORITY_FILTER: TaskPriorityType[] = [
  TaskPriorityType.Urgent,
  TaskPriorityType.Important,
  TaskPriorityType.Medium,
  TaskPriorityType.Low,
];

const loadSavedFilters = () => {
  try {
    const savedStatuses = localStorage.getItem('tasks_filter_statuses');
    const savedPriorities = localStorage.getItem('tasks_filter_priorities');
    return {
      statuses: savedStatuses ? JSON.parse(savedStatuses) : DEFAULT_STATUS_FILTER,
      priorities: savedPriorities ? JSON.parse(savedPriorities) : DEFAULT_PRIORITY_FILTER,
    };
  } catch {
    return {
      statuses: DEFAULT_STATUS_FILTER,
      priorities: DEFAULT_PRIORITY_FILTER,
    };
  }
};

const savedFilters = loadSavedFilters();

export const initialTasksState: TTasksState = {
  loading: false,
  data: undefined,
  filteredList: undefined,
  searchPhrase: '',
  selectedStatuses: savedFilters.statuses,
  selectedPriorities: savedFilters.priorities,
  detailLoading: false,
  detail: undefined,
};

export enum GetTasksList {
  REQUEST = 'GET_TASKS_LIST_REQUEST',
  SUCCESS = 'GET_TASKS_LIST_SUCCESS',
  FAILED = 'GET_TASKS_LIST_FAILED',
}

export type TGetTasksListAction =
  | { type: GetTasksList.REQUEST }
  | {
      type: GetTasksList.SUCCESS;
      payload: AxiosResponse<{
        tasks: Array<TApiTaskEntity>;
        statusOptions: Array<TTaskListOption>;
        priorityOptions: Array<TTaskListOption>;
      }>;
    }
  | { type: GetTasksList.FAILED; payload: AxiosError };

export enum FilterTasksList {
  SUBMIT = 'FILTER_TASKS_LIST',
}

export type TFilterTasksListActionPayload = {
  search: string;
  statuses: TaskStatusType[];
  priorities: TaskPriorityType[];
};

export type TFilterTasksListAction = {
  type: FilterTasksList.SUBMIT;
  payload: TFilterTasksListActionPayload;
};

export enum GetTaskDetail {
  REQUEST = 'GET_TASK_DETAIL_REQUEST',
  SUCCESS = 'GET_TASK_DETAIL_SUCCESS',
  FAILED = 'GET_TASK_DETAIL_FAILED',
}

export type TGetTaskDetailAction =
  | { type: GetTaskDetail.REQUEST }
  | {
      type: GetTaskDetail.SUCCESS;
      payload: AxiosResponse<TTaskDetailResponse>;
    }
  | { type: GetTaskDetail.FAILED; payload: AxiosError };

export enum AddTaskComment {
  REQUEST = 'ADD_TASK_COMMENT_REQUEST',
  SUCCESS = 'ADD_TASK_COMMENT_SUCCESS',
  FAILED = 'ADD_TASK_COMMENT_FAILED',
}

export type TAddTaskCommentAction =
  | { type: AddTaskComment.REQUEST }
  | {
      type: AddTaskComment.SUCCESS;
      payload: AxiosResponse<TTaskComment>;
    }
  | { type: AddTaskComment.FAILED; payload: AxiosError };

export enum DeleteTaskComment {
  REQUEST = 'DELETE_TASK_COMMENT_REQUEST',
  SUCCESS = 'DELETE_TASK_COMMENT_SUCCESS',
  FAILED = 'DELETE_TASK_COMMENT_FAILED',
}

export type TDeleteTaskCommentAction =
  | { type: DeleteTaskComment.REQUEST }
  | {
      type: DeleteTaskComment.SUCCESS;
      payload: AxiosResponse<{ commentId: number }>;
    }
  | { type: DeleteTaskComment.FAILED; payload: AxiosError };

export enum UploadTaskFile {
  REQUEST = 'UPLOAD_TASK_FILE_REQUEST',
  SUCCESS = 'UPLOAD_TASK_FILE_SUCCESS',
  FAILED = 'UPLOAD_TASK_FILE_FAILED',
}

export type TUploadTaskFileAction =
  | { type: UploadTaskFile.REQUEST }
  | {
      type: UploadTaskFile.SUCCESS;
      payload: AxiosResponse<TTaskFile>;
    }
  | { type: UploadTaskFile.FAILED; payload: AxiosError };

export enum DeleteTaskFile {
  REQUEST = 'DELETE_TASK_FILE_REQUEST',
  SUCCESS = 'DELETE_TASK_FILE_SUCCESS',
  FAILED = 'DELETE_TASK_FILE_FAILED',
}

export type TDeleteTaskFileAction =
  | { type: DeleteTaskFile.REQUEST }
  | {
      type: DeleteTaskFile.SUCCESS;
      payload: AxiosResponse<{ fileId: number }>;
    }
  | { type: DeleteTaskFile.FAILED; payload: AxiosError };

export enum UpdateTask {
  REQUEST = 'UPDATE_TASK_REQUEST',
  SUCCESS = 'UPDATE_TASK_SUCCESS',
  FAILED = 'UPDATE_TASK_FAILED',
}

export type TUpdateTaskAction =
  | { type: UpdateTask.REQUEST }
  | {
      type: UpdateTask.SUCCESS;
      payload: AxiosResponse<TTaskDetailResponse>;
    }
  | { type: UpdateTask.FAILED; payload: AxiosError };

export enum SubmitConclusion {
  REQUEST = 'SUBMIT_CONCLUSION_REQUEST',
  SUCCESS = 'SUBMIT_CONCLUSION_SUCCESS',
  FAILED = 'SUBMIT_CONCLUSION_FAILED',
}

export type TSubmitConclusionAction =
  | { type: SubmitConclusion.REQUEST }
  | {
      type: SubmitConclusion.SUCCESS;
      payload: AxiosResponse<TTaskDetailResponse>;
    }
  | { type: SubmitConclusion.FAILED; payload: AxiosError };

export type TTaskActions =
  | TGetTasksListAction
  | TFilterTasksListAction
  | TGetTaskDetailAction
  | TAddTaskCommentAction
  | TDeleteTaskCommentAction
  | TUploadTaskFileAction
  | TDeleteTaskFileAction
  | TUpdateTaskAction
  | TSubmitConclusionAction;
